import { CustomColumnDef } from '@/types/table';
import { getTableColumnLabel } from '../../utils/columnConfig';
import { FlatItem } from './components/SortableItem';

export type ChildItem = {
  columnId: string;
  label: string;
};

export type OrderItem = {
  id: string;
  label: string;
  columnIds: string[];
  children: ChildItem[];
};

export const buildOrderItems = <T>(
  columns: CustomColumnDef<T>[],
  additionalHeaderLabels?: Record<string, string>,
): OrderItem[] => {
  const items: OrderItem[] = [];
  const groupMap = new Map<string, CustomColumnDef<T>[]>();
  const ungrouped: { column: CustomColumnDef<T>; index: number }[] = [];

  for (let index = 0; index < columns.length; index++) {
    const column = columns[index];
    const groupName = column.mergedGroup;
    if (groupName) {
      const group = groupMap.get(groupName);
      if (group) {
        group.push(column);
      } else {
        groupMap.set(groupName, [column]);
        ungrouped.push({ column, index });
      }
    } else {
      ungrouped.push({ column, index });
    }
  }

  for (const { column, index } of ungrouped) {
    const groupName = column.mergedGroup;

    if (groupName) {
      const groupColumns = groupMap.get(groupName);
      if (!groupColumns) continue;
      const children: ChildItem[] = groupColumns
        .map(groupColumn => ({
          columnId: String(groupColumn.id),
          label: additionalHeaderLabels?.[String(groupColumn.id)] ?? '',
        }))
        .filter(child => child.label);

      items.push({
        id: `group:${groupName}`,
        label: groupName,
        columnIds: groupColumns.map(groupColumn => String(groupColumn.id)),
        children: children.length >= 2 ? children : [],
      });
    } else {
      const label = getTableColumnLabel(column);
      if (!label) continue;

      const columnIds = [String(column.id)];
      const children: ChildItem[] = [];

      const parentChildLabel = additionalHeaderLabels?.[String(column.id)];
      if (parentChildLabel) {
        children.push({ columnId: String(column.id), label: parentChildLabel });
      }

      let nextIndex = index + 1;
      while (
        nextIndex < columns.length &&
        !columns[nextIndex].mergedGroup &&
        !getTableColumnLabel(columns[nextIndex])
      ) {
        const childColumnId = String(columns[nextIndex].id);
        columnIds.push(childColumnId);
        const childLabel = additionalHeaderLabels?.[childColumnId];
        if (childLabel) {
          children.push({ columnId: childColumnId, label: childLabel });
        }
        nextIndex++;
      }

      items.push({
        id: columnIds[0],
        label,
        columnIds,
        children: children.length >= 2 ? children : [],
      });
    }
  }
  return items;
};

export function buildChildOrderMap(items: OrderItem[]): Map<string, ChildItem[]> {
  const childOrderMap = new Map<string, ChildItem[]>();
  items.forEach(orderItem => {
    if (orderItem.children.length > 0) {
      childOrderMap.set(orderItem.id, [...orderItem.children]);
    }
  });
  return childOrderMap;
}

export function buildItemMap(items: OrderItem[]): Map<string, OrderItem> {
  const itemMap = new Map<string, OrderItem>();
  items.forEach(orderItem => itemMap.set(orderItem.id, orderItem));
  return itemMap;
}

export function getChildrenForParent(
  parentId: string,
  childOrderMap: Map<string, ChildItem[]>,
  itemMap: Map<string, OrderItem>,
): ChildItem[] {
  return childOrderMap.get(parentId) ?? itemMap.get(parentId)?.children ?? [];
}

export function computeHasOrderChanged(
  pinnedOrder: string[],
  unpinnedOrder: string[],
  initialPinnedIds: string[],
  initialUnpinnedIds: string[],
  childOrderMap: Map<string, ChildItem[]>,
  itemMap: Map<string, OrderItem>,
): boolean {
  if (pinnedOrder.length !== initialPinnedIds.length || unpinnedOrder.length !== initialUnpinnedIds.length) return true;
  const pinnedChanged = pinnedOrder.some((orderId, index) => orderId !== initialPinnedIds[index]);
  const unpinnedChanged = unpinnedOrder.some((orderId, index) => orderId !== initialUnpinnedIds[index]);
  const childChanged = Array.from(childOrderMap.entries()).some(([parentId, children]) => {
    const originalChildren = itemMap.get(parentId)?.children ?? [];
    return children.some((child, index) => child.columnId !== originalChildren[index]?.columnId);
  });
  return pinnedChanged || unpinnedChanged || childChanged;
}

export function computeColumnIdsForOrder(
  orderIds: string[],
  itemMap: Map<string, OrderItem>,
  childOrderMap: Map<string, ChildItem[]>,
): string[] {
  return orderIds.flatMap(parentId => {
    const item = itemMap.get(parentId);
    if (!item) return [];
    const children = childOrderMap.get(parentId);
    if (children && children.length > 0) {
      const childColumnIds = children.map(child => child.columnId);
      const childIdSet = new Set(childColumnIds);
      const nonChildIds = item.columnIds.filter(columnId => !childIdSet.has(columnId));
      return [...nonChildIds, ...childColumnIds];
    }
    return item.columnIds;
  });
}

export function computeAllColumnIds(
  pinnedOrder: string[],
  unpinnedOrder: string[],
  itemMap: Map<string, OrderItem>,
  childOrderMap: Map<string, ChildItem[]>,
): string[] {
  return computeColumnIdsForOrder([...pinnedOrder, ...unpinnedOrder], itemMap, childOrderMap);
}

export function orderItemsByIds<T extends { id: string }>(items: T[], orderedIds: string[]): T[] {
  const itemMap = new Map(items.map(columnItem => [columnItem.id, columnItem]));
  const seenIds = new Set<string>();
  const orderedItems = orderedIds.flatMap(itemId => {
    const orderedItem = itemMap.get(itemId);
    if (!orderedItem) {
      return [];
    }

    seenIds.add(itemId);
    return [orderedItem];
  });

  return [...orderedItems, ...items.filter(columnItem => !seenIds.has(columnItem.id))];
}

const areIdsEqual = (leftIds: string[], rightIds: string[]): boolean => {
  if (leftIds.length !== rightIds.length) {
    return false;
  }

  return leftIds.every((leftId, index) => leftId === rightIds[index]);
};

export function reconcileOrderIds(currentOrder: string[], nextOrder: string[]): string[] {
  const nextOrderIdSet = new Set(nextOrder);
  const preservedOrder = currentOrder.filter(orderId => nextOrderIdSet.has(orderId));
  const preservedOrderIdSet = new Set(preservedOrder);
  const appendedOrder = nextOrder.filter(orderId => !preservedOrderIdSet.has(orderId));
  const reconciledOrder = [...preservedOrder, ...appendedOrder];

  return areIdsEqual(currentOrder, reconciledOrder) ? currentOrder : reconciledOrder;
}

const reconcileChildItems = (currentChildren: ChildItem[], nextChildren: ChildItem[]): ChildItem[] => {
  const nextChildMap = new Map(nextChildren.map(nextChild => [nextChild.columnId, nextChild]));
  const preservedChildIds = currentChildren
    .map(currentChild => currentChild.columnId)
    .filter(childColumnId => nextChildMap.has(childColumnId));
  const preservedChildIdSet = new Set(preservedChildIds);
  const appendedChildIds = nextChildren
    .map(nextChild => nextChild.columnId)
    .filter(childColumnId => !preservedChildIdSet.has(childColumnId));
  const reconciledChildIds = [...preservedChildIds, ...appendedChildIds];

  if (
    areIdsEqual(
      currentChildren.map(currentChild => currentChild.columnId),
      reconciledChildIds,
    )
  ) {
    return currentChildren;
  }

  return reconciledChildIds.flatMap(childColumnId => {
    const nextChild = nextChildMap.get(childColumnId);
    return nextChild ? [nextChild] : [];
  });
};

export function reconcileChildOrderMap(
  currentChildOrderMap: Map<string, ChildItem[]>,
  nextChildOrderMap: Map<string, ChildItem[]>,
): Map<string, ChildItem[]> {
  let hasChanges = currentChildOrderMap.size !== nextChildOrderMap.size;
  const reconciledChildOrderMap = new Map<string, ChildItem[]>();

  for (const [parentId, nextChildren] of nextChildOrderMap.entries()) {
    const currentChildren = currentChildOrderMap.get(parentId);

    if (!currentChildren) {
      hasChanges = true;
      reconciledChildOrderMap.set(parentId, nextChildren);
      continue;
    }

    const reconciledChildren = reconcileChildItems(currentChildren, nextChildren);
    if (reconciledChildren !== currentChildren) {
      hasChanges = true;
    }

    reconciledChildOrderMap.set(parentId, reconciledChildren);
  }

  return hasChanges ? reconciledChildOrderMap : currentChildOrderMap;
}

export function flattenItems(
  orderIds: string[],
  itemMap: Map<string, OrderItem>,
  childOrderMap: Map<string, ChildItem[]>,
): FlatItem[] {
  const flatItems: FlatItem[] = [];
  for (const parentId of orderIds) {
    const item = itemMap.get(parentId);
    if (!item) continue;
    flatItems.push({ id: parentId, label: item.label, parentId: null });
    const children = childOrderMap.get(parentId) ?? item.children;
    for (const child of children) {
      flatItems.push({ id: `child:${child.columnId}`, label: child.label, parentId });
    }
  }
  return flatItems;
}

export function reorderFlatItems(
  flatItems: FlatItem[],
  activeId: string,
  overId: string,
): { newParentOrder: string[]; newChildOrderMap: Map<string, string[]> } | null {
  const activeItem = flatItems.find(flatItem => flatItem.id === activeId);
  const overItem = flatItems.find(flatItem => flatItem.id === overId);
  if (!activeItem || !overItem) return null;

  const isActiveChild = activeItem.parentId !== null;
  const isOverChild = overItem.parentId !== null;

  if (isActiveChild && isOverChild && activeItem.parentId === overItem.parentId) {
    if (!activeItem.parentId) return null;
    const parentId = activeItem.parentId;
    const childIds = flatItems.filter(flatItem => flatItem.parentId === parentId).map(flatItem => flatItem.id);
    const oldIndex = childIds.indexOf(activeId);
    const newIndex = childIds.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return null;
    const reordered = [...childIds];
    reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, activeId);
    const newChildOrderMap = new Map<string, string[]>();
    newChildOrderMap.set(
      parentId,
      reordered.map(childId => childId.replace('child:', '')),
    );
    return {
      newParentOrder: flatItems.filter(flatItem => flatItem.parentId === null).map(flatItem => flatItem.id),
      newChildOrderMap,
    };
  }

  if (!isActiveChild && !isOverChild) {
    const parentIds = flatItems.filter(flatItem => flatItem.parentId === null).map(flatItem => flatItem.id);
    const oldIndex = parentIds.indexOf(activeId);
    const newIndex = parentIds.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return null;
    const reordered = [...parentIds];
    reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, activeId);
    return { newParentOrder: reordered, newChildOrderMap: new Map() };
  }

  return null;
}
